import React, { FC, Fragment, Suspense, useState } from 'react';
import {
  Box,
  FormControl,
  Text,
  SideSheetFooter,
  TextArea,
  TextFieldSize,
  Spinner,
  SeparatorType,
  Separator,
  useModalState,
} from '@bechtel/gds';
import {
  AddMilestoneAttachmentError,
  Attachment,
  AttachmentMetadataInput,
  MilestoneStatusCode,
  ReclaimMilestoneInput,
  ReclaimMilestonePayload,
  RejectWeldReason,
  RejectWeldSubReason,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { FieldArray, Formik, FormikProps, Field } from 'formik';
import produce from 'immer';
import { get, join, map } from 'lodash';
import { array, object, string } from 'yup';
import {
  FileUpload,
  FileUploadInput,
  FileUploadPreviewImg,
  FileUploadResponse,
  ErrorBoundary,
  SuspenseLoader,
  ViewMilestoneAttachment,
  SectionHeader,
} from '@bechtel/client-common';
import { ApolloError, useMutation } from '@apollo/client';
import { DELETE_BLOCK_MEDIA, RECLAIM_MILESTONE } from '@bechtel/client-gql';
import { AttachmentHeadingStyled, FooterButtonStyled } from './styled';
import { AddAttachmentError } from './error';
import { MilestoneError } from '../../error';
import { ReclaimProps, FormikState } from './types';
import { SendBackReasonMessages, SendBackSubReasonMessages } from '@bechtel/client-weld-common';
import { useLingui } from '@lingui/react';
import { ConfirmClaimAction } from '~/components/molecules/confirm';

const initialValues: FormikState = {
  comments: '',
  mediaMetadata: [],
};

const addMilestoneAttachmentSchema = object().shape({
  comments: string()
    .min(2, t`Too Short!`)
    .max(2000, t`Too Long!`)
    .required(t`Comment is mandatory`),
  mediaMetadata: array()
    .required()
    .max(3, t`Max 3 photos can be uploaded`),
});

export const Reclaim: FC<ReclaimProps> = ({
  milestone,
  closeModal,
  refetchQueries,
  showSubHeader = true,
  showItrCertDialog = false,
}) => {
  const { i18n } = useLingui();
  const attachments: Attachment[] = milestone?.attachments ? milestone.attachments : [];
  const [addAttachmentError, setAddAttachmentError] = useState<AddMilestoneAttachmentError | ApolloError | null>();
  const [deleteBlockMedia, { loading: deleteLoading, data: deleteData }] = useMutation(DELETE_BLOCK_MEDIA);
  const deleteError = get(deleteData, 'deleteBlockMedia.error', null);
  const rejectionSubReasonTypes = milestone?.currentStatus?.rejectionSubReasonTypes;
  const { visible, open, close } = useModalState(false);
  const [formikValues, setFormikValues] = useState<FormikState>();

  const [reclaimMilestone, { loading, data, error }] = useMutation<
    { reclaimMilestone: ReclaimMilestonePayload },
    { input: ReclaimMilestoneInput }
  >(RECLAIM_MILESTONE, {
    onCompleted: () => {
      closeModal();
    },
    refetchQueries: refetchQueries,
  });
  const milestoneError = get(data, 'reclaimMilestone.error') || error;

  const onCertifyClick = async () => {
    reclaim(formikValues, true);
  };

  const reclaim = async (values: FormikState, fromCertDialog: boolean = false) => {
    reclaimMilestone({
      variables: {
        input: {
          comments: values.comments,
          attachmentMetadata: values.mediaMetadata,
          commodityMilestoneId: milestone.id,
          itrRequired: fromCertDialog,
        },
      },
    });
  };

  return (
    <Fragment>
      {milestoneError && <MilestoneError milestoneError={milestoneError} />}
      {visible && !milestoneError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ConfirmClaimAction onCertifyClick={onCertifyClick} isMutationInProgress={loading} close={close} />
          </Suspense>
        </ErrorBoundary>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={addMilestoneAttachmentSchema}
        onSubmit={(values: FormikState) => {
          if (showItrCertDialog) {
            setFormikValues(values);
            open();
          } else {
            reclaim(values);
          }
        }}
      >
        {({ isValid, dirty, handleSubmit, values }: FormikProps<FormikState>) => {
          return (
            <Fragment>
              {showSubHeader && (
                <Box
                  flexDirection={{ sm: 'column' }}
                  px={{ sm: '500' }}
                  py={{ sm: '450' }}
                  data-testid="reclaim-header"
                >
                  <Text size="400" weight="600">
                    {milestone?.currentStatus?.codeI18n == MilestoneStatusCode.REJECTED ? t`Reclaim` : t`Claim`}
                  </Text>
                  <Box mt={{ sm: '300' }} alignItems={{ sm: 'center' }}>
                    <Text size="400" weight="600">
                      <Trans>Milestone</Trans>
                    </Text>
                    <Text size="400" weight="400">
                      {`: ${milestone?.name}`}
                    </Text>
                  </Box>
                </Box>
              )}
              {milestone?.currentStatus?.rejectionType?.reason && (
                <>
                  <SectionHeader title={t`Sent Back Reason`} />
                  <Box
                    flexDirection={{ sm: 'column' }}
                    px={{ sm: '450' }}
                    pt={{ sm: '500' }}
                    pb={rejectionSubReasonTypes && rejectionSubReasonTypes.length > 0 ? { sm: '450' } : { sm: '500' }}
                  >
                    <Text size="400" color="grey.1000" weight="600">
                      <Trans>Reason</Trans>
                    </Text>
                    <Text size="400" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
                      {milestone?.currentStatus?.rejectionType?.reason == RejectWeldReason.OTHER_REASON
                        ? milestone?.currentStatus?.comments
                        : i18n._(SendBackReasonMessages[milestone?.currentStatus?.rejectionType?.reason])}
                    </Text>
                  </Box>
                  {rejectionSubReasonTypes && rejectionSubReasonTypes.length > 0 && (
                    <>
                      <Separator type={SeparatorType.HORIZONTAL} />
                      <Box flexDirection={{ sm: 'column' }} px={{ sm: '450' }} pb={{ sm: '500' }} pt={{ sm: '400' }}>
                        <Text size="400" color="grey.1000" weight="600">
                          <Trans> Sub Reason(s)</Trans>
                        </Text>
                        <Text size="400" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
                          {join(
                            map(rejectionSubReasonTypes, (item) => {
                              return item.reason == RejectWeldSubReason.OTHER_SUB_REASON
                                ? item.comments
                                : i18n._(SendBackSubReasonMessages[item.reason]);
                            }),
                            ', '
                          )}
                        </Text>
                      </Box>
                    </>
                  )}
                </>
              )}
              <Box
                flexDirection={{ sm: 'column' }}
                flexGrow={{ sm: 1 }}
                css={{ overflowY: 'auto' }}
                data-testid="reclaim-details"
              >
                <Box flexDirection={{ sm: 'column' }}>
                  <AttachmentHeadingStyled>
                    <Trans>Resolution Details</Trans>
                  </AttachmentHeadingStyled>
                  <Field name="comments">
                    {({ field }) => {
                      return (
                        <Box
                          px={{ sm: '500' }}
                          py={{ sm: '600' }}
                          flexDirection={{ sm: 'column' }}
                          flexGrow={{ sm: 1 }}
                        >
                          <FormControl label={t`Add a comment (Required)`}>
                            <TextArea
                              maxLength={2000}
                              data-testid="comments"
                              size={TextFieldSize.LARGE}
                              rows="3"
                              {...field}
                            ></TextArea>
                          </FormControl>
                        </Box>
                      );
                    }}
                  </Field>
                </Box>
                <Box flexDirection={{ sm: 'column' }}>
                  <AttachmentHeadingStyled>
                    <Trans>Attach Photos</Trans> ({values?.mediaMetadata?.length ? values.mediaMetadata.length : 0}
                    /3)
                  </AttachmentHeadingStyled>
                  <Box flexDirection={{ sm: 'column' }} py={{ sm: '600' }} px={{ sm: '500' }}>
                    <FieldArray name="mediaMetadata">
                      {({ form, push, remove }) => {
                        return (
                          <FormControl label={<Trans>Upload a Photo</Trans>} css={{ flexGrow: 1 }}>
                            {!deleteError ? (
                              <FileUpload
                                filesCount={form?.values?.mediaMetadata?.length ? form.values.mediaMetadata.length : 0}
                                maxFiles={3}
                                maxFileSizeMB={10}
                                acceptFileTypes="image/jpeg, image/png"
                                onSuccess={(res: FileUploadResponse, file) => {
                                  const newMediaData: AttachmentMetadataInput = {
                                    fileName: file.name,
                                    fileSize: file.size,
                                    attachmentId: res.attachmentId,
                                    mimeType: file.type,
                                  };
                                  push(newMediaData);
                                }}
                              >
                                {({ getInputProps, getRootProps, loading, files, setFiles, acceptFileTypes }) => {
                                  return (
                                    <Fragment>
                                      <FileUploadInput
                                        getInputProps={getInputProps}
                                        getRootProps={getRootProps}
                                        loading={loading || deleteLoading}
                                        acceptFileTypes={acceptFileTypes}
                                      />
                                      {deleteError && (
                                        <Box my={{ sm: '200' }}>
                                          <Trans>There was an error when deleting a file.</Trans>
                                        </Box>
                                      )}
                                      {map(form.values.mediaMetadata, (file, index: number) => {
                                        return (
                                          <Box
                                            mb={{ sm: '500' }}
                                            key={`${file.name}.${index}`}
                                            flexDirection={{
                                              sm: 'column',
                                            }}
                                          >
                                            <FileUploadPreviewImg
                                              file={files[index]}
                                              onDelete={async () => {
                                                const { data } = await deleteBlockMedia({
                                                  variables: {
                                                    input: {
                                                      attachmentId: files[index].attachmentId,
                                                    },
                                                  },
                                                });

                                                if (!data.deleteBlockAttachment.error) {
                                                  remove(index);
                                                  setFiles(
                                                    produce((draft) => {
                                                      // @ts-ignore declaration throwing unknownn type error for draft
                                                      draft.splice(index, 1);
                                                    })
                                                  );
                                                }
                                              }}
                                            />
                                          </Box>
                                        );
                                      })}
                                    </Fragment>
                                  );
                                }}
                              </FileUpload>
                            ) : (
                              <div>
                                <Trans>There was an error when deleting the file</Trans>
                              </div>
                            )}
                          </FormControl>
                        );
                      }}
                    </FieldArray>
                  </Box>
                </Box>
                {attachments?.length > 0 && (
                  <Box flexDirection={{ sm: 'column' }} data-testid="reclaim-attachments">
                    <Box>
                      <AttachmentHeadingStyled>
                        <Trans>Attachments</Trans>
                      </AttachmentHeadingStyled>
                    </Box>
                    <ViewMilestoneAttachment attachments={attachments} />
                  </Box>
                )}
              </Box>

              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '300' }}>
                    <FooterButtonStyled onClick={closeModal}>
                      <Trans>Cancel</Trans>
                    </FooterButtonStyled>
                  </Box>
                  <Box>
                    <FooterButtonStyled disabled={loading || !isValid || !dirty} onClick={() => handleSubmit()}>
                      {loading ? <Spinner /> : <Trans>Reclaim</Trans>}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </Fragment>
          );
        }}
      </Formik>

      <ErrorBoundary>
        <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
          {addAttachmentError && (
            <AddAttachmentError
              error={addAttachmentError}
              close={() => {
                setAddAttachmentError(null);
              }}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    </Fragment>
  );
};
